import React from 'react';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';

function getInitialState() {
  return {
    finicityConfigResource: null,
    finicityConfigCollection: null,
    isInitialized: false,
    initializationError: null,
  };
}

export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

function reducer(state, action) {
  switch (action.type) {
    case INITIALIZE_SUCCEEDED: {
      const [finicityConfigResource] = action.finicityConfigCollection;
      return {
        ...state,
        isInitialized: true,
        finicityConfigResource,
        finicityConfigCollection: action.finicityConfigCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        initializationError: action.initializationError,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

export default function useFinicityConnect(resource, elementId, args = {}) {
  const {
    onSuccess,
    onError,
    onClose,
    onRoute,
    onUser,
    onLoaded,
  } = args;
  const [state, dispatch] = React.useReducer(reducer, null, getInitialState);

  const handleClickInitiate = React.useCallback(() => {
    if (!state.isInitialized) {
      return;
    }

    const { finicityConfigResource } = state;
    const connectUrl = finicityConfigResource.attr('url');

    window.finicityConnect?.launch(connectUrl, {
      selector: `#${elementId}`,
      overlay: 'rgba(255,255,255, 0)',
      success(data) {
        window.finicityConnect?.destroy();
        // will be called on success. For list of possible message, refer to onSuccess(data) Method.
        if (typeof onSuccess === 'function') {
          return onSuccess(data, finicityConfigResource);
        }

        return null;
      },
      error(data) {
        window.finicityConnect?.destroy();
        // will be called on success. For list of possible message, refer to onSuccess(data) Method.
        if (typeof onError === 'function') {
          return onError(data, finicityConfigResource);
        }

        return null;
      },
      cancel(data) {
        window.finicityConnect?.destroy();
        // will be called on success. For list of possible message, refer to onSuccess(data) Method.
        if (typeof onClose === 'function') {
          return onClose(data, finicityConfigResource);
        }

        return null;
      },
      route(event) {
        // will be called on success. For list of possible message, refer to onSuccess(data) Method.
        if (typeof onRoute === 'function') {
          return onRoute(event, finicityConfigResource);
        }

        return null;
      },
      loaded(event) {
        // will be called on success. For list of possible message, refer to onSuccess(data) Method.
        if (typeof onLoaded === 'function') {
          return onLoaded(event, finicityConfigResource);
        }

        return null;
      },
      user(event) {
        // will be called on success. For list of possible message, refer to onSuccess(data) Method.
        if (typeof onUser === 'function') {
          return onUser(event, finicityConfigResource);
        }

        return null;
      },
    },
    elementId);
  }, [state]);

  const onMount = async () => {
    try {
      // Expect the finicity link relationship to be here at this point. Checked higher up.
      const finicityConfigCollection = await resource.relationship(fr.relationships.finicityLink.name).link('related').get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        finicityConfigCollection,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  const onDismount = () => {
    window.finicityConnect?.destroy(); // eslint-disable-line
  };

  useComponentDidMount(onMount, onDismount);

  return {
    handleClickInitiate,
    isInitialized: state.isInitialized,
    initializationError: state.initializationError,
  };
}
